import { DialogService } from 'src/app/core/services/dialog.service';
import { ToolbarService } from 'src/app/core/services/toolbar.service';
import { AfterViewInit, Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, filter, map, startWith, switchMap, take, tap } from 'rxjs/operators';
import { CustomerBiller, IBillerShort } from 'src/app/billers/models/model';
import { AnalyticsFormService } from 'src/app/core/services/analytics-form.service';
import { AppConfig } from 'src/app/core/services/app-config.service';
import { HandsetService } from 'src/app/core/services/handset.service';
import { AuthStoreService } from 'src/app/shared/services/auth/auth-store.service';
import { AddLiabilityManualForm } from '../../forms/add-liability-manual.form';
import { LiabilityApiService } from '../../services/liability-api.service';
import { Observable, of } from 'rxjs';
import { CustomerBillerStoreService } from 'src/app/billers/services/customer-biller-store.service';
import { BillerService } from '../../../billers/services/biller.service';
import { ISavedLiability } from '../../models/liability.model';
import { DatePipe } from '@angular/common';
import { AddManualLiabilityData } from '../../models/add-manual-liability-data.model';
import { ISO_PATTERN } from '../../../shared/configs/date-config';
import { LoaderButtonType } from '../../../shared/models/loader-button-type.enum';

@Component({
  standalone: false,
  selector: 'app-add-liability-manual',
  templateUrl: './add-liability-manual.component.html',
  styleUrls: ['./add-liability-manual.component.scss'],
  providers: [DatePipe],
})
export class AddLiabilityManualComponent implements OnInit, AfterViewInit {

  @Input()
  title: string;

  @Input()
  subtitle: string;

  @Input()
  buttonText: string;
  private _initialValue: AddManualLiabilityData | undefined;

  @Input()
  set initialValue(newVal: AddManualLiabilityData | undefined) {
    this._initialValue = newVal;
    if (this._initialValue !== undefined && this._initialValue !== null) {
      this.form = new AddLiabilityManualForm(this.MIN_AMOUNT,
        this.appConfig.config.liabilityAmountLimit,
        this._initialValue.amount,
        this._initialValue.biller,
        this._initialValue.billerId,
        this._initialValue.bban,
        this._initialValue.nip,
        this._initialValue.paymentDue,
        this._initialValue.title);
      if (this.form.billerId.value === null || this.form.billerId.value === '') {
        this.showNipControl = true;
      }
    }
  }

  get initialValue() {
    return this._initialValue;
  }

  public customerBillers: CustomerBiller[] = [];
  filteredOptions: Observable<IBillerShort[]>;
  isSubmitted = false;
  showNipControl = false;
  submitting = false;
  public form: AddLiabilityManualForm;
  public savedLiability: ISavedLiability = {
    id: null,
    inVerification: false,
  };

  minDate: Date;
  maxDate: Date;
  isoPattern: string = ISO_PATTERN;

  public formName = 'newLiability';
  public readonly MIN_AMOUNT = 0.01;

  constructor(
    public appConfig: AppConfig,
    public handsetService: HandsetService,
    private auth: AuthStoreService,
    private router: Router,
    private route: ActivatedRoute,
    private toolbar: ToolbarService,
    private dialog: DialogService,
    private liabilityApiService: LiabilityApiService,
    private customerBillerStoreService: CustomerBillerStoreService,
    private billerService: BillerService,
    private analyticsFormService: AnalyticsFormService,
  ) {
    this.toolbar.init(this.route);

    this.minDate = new Date();
    this.maxDate = new Date();
    this.minDate.setMonth(this.minDate.getMonth() - 3);
    this.maxDate.setMonth(this.maxDate.getMonth() + 12);
  }

  public ngOnInit(): void {
    this.loadData();
    if (this.initialValue === undefined || this.initialValue === null) {
      this.form = new AddLiabilityManualForm(this.MIN_AMOUNT,
        this.appConfig.config.liabilityAmountLimit,
        null, null, null, null, null, null, null);
    } else {
      this.form = new AddLiabilityManualForm(this.MIN_AMOUNT,
        this.appConfig.config.liabilityAmountLimit,
        this.initialValue.amount,
        this.initialValue.biller,
        this.initialValue.billerId,
        this.initialValue.bban,
        this.initialValue.nip,
        this.initialValue.paymentDue,
        this.initialValue.title);
      if (this.form.billerId.value === null || this.form.billerId.value === '') {
        this.showNipControl = true;
      }
    }
    this.form.biller.valueChanges
      .pipe(
        filter((value) => !!value),
        tap((value) => {
          const customerBiller: CustomerBiller = this.customerBillers
            .filter((cb) => cb.biller.name === value)[0];
          this.form.patchValue({
            billerId: customerBiller?.biller.code,
          });
        }),
      )
      .subscribe();
  }

  ngAfterViewInit() {
    this.filteredOptions = this.form.biller.valueChanges.pipe(
      startWith(this.form.biller.value || ''),
      distinctUntilChanged(),
      debounceTime(400),
      switchMap(val => {
        if (val !== '' && val.length > 1) {
          this.submitting = true;
          return this.billerService.getBillers(val).pipe(
            tap(() => {
              this.submitting = false;
            }),
            map(billers => {
              const matchingBiller = billers.find(biller => biller.name === val);
              if (matchingBiller !== null && matchingBiller !== undefined) {
                this.setBiller(matchingBiller);
                this.showNipControl = false;
                this.form.nip.setValue(null);
              } else {
                this.setBiller(null);
                this.showNipControl = true;
              }
              return billers;
            }),
          );
        } else {
          this.showNipControl = null;
          this.form.nip.setValue(null);
          this.setBiller(null);
          return of([]);
        }
      }),
    );
  }

  onBillerBlur() {
    if (this.form.biller.value !== '' && (this.form.billerId.value === null || this.form.billerId.value === '')) {
      this.showNipControl = true;
    }
  }

  public onSubmit = () => {
    this.submitting = true;
    if (this.form.invalid) {
      this.isSubmitted = true;
      this.form.markAllAsTouched();
      this.analyticsFormService.sendEventForInvalidControls(this.form.controls, this.formName);
      this.submitting = false;
      return of({});
    }

    const data = this.form.getFinalData(this.auth.userInfo.customer_id);
    return this.liabilityApiService
      .saveManualLiabilities(data)
      .pipe(map((value: ISavedLiability) => {
        this.savedLiability = value;
        this.isSubmitted = false;
        this.submitting = false;
        this.form.reset();
      }));
  };

  setBiller(biller: IBillerShort) {
    if (biller !== undefined && biller !== null) {
      this.form.billerId.setValue(biller.id);
    } else {
      this.form.billerId.setValue(null);
    }
  }

  private getFullCustomerBiller(customerBillerId: string) {
    this.customerBillerStoreService
      .getCustomerBiller(customerBillerId)
      .pipe(tap((customerBiller) => this.customerBillers.push(customerBiller)))
      .subscribe();
  }

  private loadData(): void {
    this.liabilityApiService
      .getCustomerBillersWithManualSource()
      .pipe(take(1))
      .subscribe((customerBillers) => (this.customerBillers = customerBillers));
  }

  onGoToLiability() {
    const navigationExtras: NavigationExtras = { relativeTo: this.route };
    this.router.navigateByUrl('/liabilities/' + this.savedLiability.id, navigationExtras);
  }

  onGoBackClick() {
    if (this.initialValue && this.initialValue.billerId) {
      this.router.navigateByUrl('/billers');
    } else {
      this.router.navigate(['../'], { relativeTo: this.route });
    }
  }

  onAddNextLiability() {
    this.savedLiability = {
      id: null,
      inVerification: null,
    };
    this.router.navigateByUrl('/liabilities/add/manual');

  }

  get isNipRequired(): boolean {
    return this.form.errors && this.form.errors.hasOwnProperty('billerIdOrNipRequired');
  }

  protected readonly LoaderButtonType = LoaderButtonType;
}
